import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { validatePhone } from "@/lib/phone";
import { rupees } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** `open`/`close` "HH:MM" + a slot length -> the list of non-overlapping windows between them.
 * A window that would run past `close` is dropped rather than clipped. */
function slotWindows(open, close, slotMinutes) {
  const toMinutes = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
  };
  const fmt = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  const openM = toMinutes(open);
  const closeM = toMinutes(close);
  const windows = [];
  for (let start = openM; start + slotMinutes <= closeM; start += slotMinutes) {
    windows.push({ start: fmt(start), end: fmt(start + slotMinutes) });
  }
  return windows;
}

/** The next date (today included) whose weekday is `mondayIndex` — `directory/hours.py`'s
 * convention, Monday = 0 .. Sunday = 6 (the same one `opening_hours`/`weekdayShort` use), not
 * JS's `getDay()` (Sunday = 0). */
function nextDateForWeekday(mondayIndex) {
  const today = new Date();
  const todayIndex = (today.getDay() + 6) % 7;
  let diff = mondayIndex - todayIndex;
  if (diff < 0) diff += 7;
  const d = new Date(today);
  d.setDate(d.getDate() + diff);
  return d;
}

/** `date` + a slot window's "HH:MM" start -> the same `datetime-local` input-value shape the
 * free-form picker below produces, so both paths feed `preferred_at` identically. */
function datetimeLocalValue(date, hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Public "book a service visit" form on a business mini-site (shops with the `service_visit`
 * capability). Pick a listed service (a catalog item in the "Services" section) or type one, give
 * the visit address + a preferred time + contact, and submit. Posts to the city-scoped,
 * per-business-throttled capture endpoint; business by path id, city by the signed city-token,
 * never by the form. Mirrors EnquirySection. `services` is the shop's catalog items, optional.
 *
 * PR4 (`design_handoff_business_websites`): when the owner has configured
 * `storefront_config.slots` (`{days, open, close, slot_minutes, visit_fee_minor_units}`), the
 * free-form "preferred date & time" input is replaced by a day picker (only the configured
 * `days`) and a time-window picker generated from `open`/`close`/`slot_minutes`, plus the visit
 * fee. Both paths write the exact same `form.preferred_at` field and POST to the exact same
 * endpoint — no new request field, no endpoint change. Without `slots` this component is
 * byte-for-byte the form it always was. */
export function ServiceVisitSection({ business, services = [] }) {
  const { t } = useApp();
  const slots = business.storefront_config?.slots;
  const [form, setForm] = useState({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    catalog_item_id: "",
    service_type: "",
    address: "",
    preferred_at: "",
  });
  const [slotDay, setSlotDay] = useState(null);
  const [slotWindow, setSlotWindow] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const windows = useMemo(
    () => (slots ? slotWindows(slots.open, slots.close, slots.slot_minutes) : []),
    [slots],
  );

  const pickSlot = (day, window) => {
    setSlotDay(day);
    setSlotWindow(window);
    setForm((f) => ({ ...f, preferred_at: datetimeLocalValue(nextDateForWeekday(day), window) }));
  };

  const canSend =
    (form.contact_phone.trim() !== "" || form.contact_email.trim() !== "") &&
    form.address.trim() !== "" &&
    (!slots || (slotDay !== null && slotWindow !== ""));

  const submit = async () => {
    if (!canSend) return;
    // R11 PHONE: the same rule the server applies, in the visitor's language, before the round trip.
    const phoneCheck = validatePhone(form.contact_phone, "mobile");
    if (!phoneCheck.ok) {
      toast.error(t(phoneCheck.messageKey));
      return;
    }
    setBusy(true);
    try {
      const picked = services.find((s) => s.id === form.catalog_item_id);
      await api.post(`/city/businesses/${business.id}/service-visits`, {
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim(),
        contact_email: form.contact_email.trim(),
        catalog_item_id: form.catalog_item_id || null,
        // Free-text falls back to the picked catalog item's name so the provider always sees what.
        service_type: form.service_type.trim() || (picked ? picked.name : ""),
        address: form.address.trim(),
        preferred_at: form.preferred_at ? new Date(form.preferred_at).toISOString() : null,
      });
      setForm({
        contact_name: "",
        contact_phone: "",
        contact_email: "",
        catalog_item_id: "",
        service_type: "",
        address: "",
        preferred_at: "",
      });
      setSlotDay(null);
      setSlotWindow("");
      setSent(true);
      toast.success(t("serviceVisit.sent"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-border p-6 text-center" data-testid="visit-sent">
        <p className="text-sm text-muted-foreground">{t("serviceVisit.sentBody")}</p>
        <Button
          variant="outline"
          className="mt-4 rounded-full"
          data-testid="visit-another"
          onClick={() => setSent(false)}
        >
          {t("serviceVisit.sendAnother")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-border p-5" data-testid="visit-section">
      <p className="text-sm text-muted-foreground">{t("serviceVisit.intro")}</p>
      {services.length > 0 && (
        <div className="space-y-1.5">
          <Label htmlFor="visit-service">{t("serviceVisit.service")}</Label>
          <select
            id="visit-service"
            data-testid="visit-service"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={form.catalog_item_id}
            onChange={(e) => setForm({ ...form, catalog_item_id: e.target.value })}
          >
            <option value="">{t("serviceVisit.serviceOther")}</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {!form.catalog_item_id && (
        <div className="space-y-1.5">
          <Label htmlFor="visit-type">{t("serviceVisit.serviceType")}</Label>
          <Input
            id="visit-type"
            data-testid="visit-type"
            value={form.service_type}
            onChange={(e) => setForm({ ...form, service_type: e.target.value })}
          />
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="visit-address">{t("serviceVisit.address")}</Label>
        <Textarea
          id="visit-address"
          rows={2}
          data-testid="visit-address"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="visit-phone">{t("serviceVisit.phone")}</Label>
          <Input
            id="visit-phone"
            data-testid="visit-phone"
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="visit-email">{t("serviceVisit.email")}</Label>
          <Input
            id="visit-email"
            type="email"
            data-testid="visit-email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="visit-name">{t("serviceVisit.name")}</Label>
        <Input
          id="visit-name"
          data-testid="visit-name"
          value={form.contact_name}
          onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
        />
      </div>
      {slots ? (
        <div className="space-y-4" data-testid="visit-slots">
          <div className="space-y-1.5">
            <Label>{t("serviceVisit.chooseDay")}</Label>
            <div className="flex flex-wrap gap-2" data-testid="visit-slot-days">
              {slots.days.map((day) => (
                <button
                  key={day}
                  type="button"
                  data-testid={`visit-slot-day-${day}`}
                  onClick={() => pickSlot(day, slotWindow || windows[0]?.start || "")}
                  className={`rounded-full border px-3 py-1.5 text-sm ${
                    slotDay === day
                      ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                      : "border-border"
                  }`}
                >
                  {t("storefront.slots.weekdayShort")[day]}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("serviceVisit.chooseWindow")}</Label>
            <div className="flex flex-wrap gap-2" data-testid="visit-slot-windows">
              {windows.map((w) => (
                <button
                  key={w.start}
                  type="button"
                  disabled={slotDay === null}
                  data-testid={`visit-slot-window-${w.start}`}
                  onClick={() => pickSlot(slotDay, w.start)}
                  className={`rounded-full border px-3 py-1.5 tabular-nums text-xs ${
                    slotWindow === w.start
                      ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                      : "border-border"
                  }`}
                >
                  {w.start}–{w.end}
                </button>
              ))}
            </div>
          </div>
          <p className="tabular-nums text-xs text-muted-foreground" data-testid="visit-slot-fee">
            {t("serviceVisit.visitFee")}: {rupees({ minor_units: slots.visit_fee_minor_units })}
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="visit-preferred">{t("serviceVisit.preferredAt")}</Label>
          <Input
            id="visit-preferred"
            type="datetime-local"
            data-testid="visit-preferred"
            value={form.preferred_at}
            onChange={(e) => setForm({ ...form, preferred_at: e.target.value })}
          />
        </div>
      )}
      <Button
        className="rounded-full"
        disabled={busy || !canSend}
        data-testid="visit-submit"
        onClick={submit}
      >
        {t("serviceVisit.submit")}
      </Button>
    </div>
  );
}
